import type { MapViewForListingRequest, RequestedListing } from "../@types/mapviewxs";

export const validatedDistance = (distance: string): MapViewForListingRequest["distance"] | null => {
  try {
    const parsed = JSON.parse(distance);

    if (typeof parsed !== "object") {
      return null;
    }

    const number = parseInt(parsed.value);
    return isNaN(number)
      ? null
      : {
          value: number,
          unit: parsed.unit,
        };
  } catch {
    // broken JSON
    return null;
  }
};

export const validatedDistanceNumber = (distance: string): string | null => {
  const number = parseInt(distance);
  return isNaN(number) ? null : number.toString();
};

export const validatedSources = (sources: string): string | null => (sources.split(",")[0].length === 0 ? null : sources);

export const validatedZipcode = (zipcode: string | undefined | null): string | null =>
  zipcode?.length === 0 ? null : zipcode ? zipcode.toUpperCase() : null;

export const validatedListings = (listings: string): RequestedListing[] | null => {
  try {
    const parsed = JSON.parse(listings);
    if (!Array.isArray(parsed)) {
      return null;
    }

    // filter out invalid listings & map {id} to RequestedListing
    const listingList: RequestedListing[] = [];
    for (const parsedListing of parsed) {
      if (isValidParsedListing(parsedListing)) {
        listingList.push({ id: parsedListing.id, sponsored: parsedListing.isSp, trackingParams: parsedListing.trackingParams });
      }
    }

    return listingList.length === 0 ? null : listingList;
  } catch {
    // broken JSON
    return null;
  }
};

/**
 * Type guard for parsed listing from URL query parameter
 */
export function isValidParsedListing(
  parsedListing: unknown,
): parsedListing is { id: string; isSp?: boolean; trackingParams?: Record<string, string> } {
  const isAnObject = typeof parsedListing === "object" && parsedListing !== null;
  if (!isAnObject) return false;

  const hasValidId = "id" in parsedListing && typeof parsedListing.id === "string";
  if (!hasValidId) return false;

  const hasInvalidTrackingParams = "trackingParams" in parsedListing && typeof parsedListing.trackingParams !== "object";
  if (hasInvalidTrackingParams) return false;

  // check for optional isSp field
  return "isSp" in parsedListing ? typeof parsedListing.isSp === "boolean" || typeof parsedListing.isSp === "undefined" : true;
}

export interface ZipcodeValidation {
  validationPattern?: string;
  minCharacterCount?: number;
  maxCharacterCount?: number;
}

/**
 * Validates zipcode length against min/max character count constraints
 */
export function isValidZipcodeLength(zipcode: string, validation: ZipcodeValidation): boolean {
  const { minCharacterCount, maxCharacterCount } = validation;
  if (typeof maxCharacterCount === "number" && zipcode.length > maxCharacterCount) return false;
  if (typeof minCharacterCount === "number" && zipcode.length < minCharacterCount) return false;
  return true;
}

/**
 * Validates zipcode against a regex pattern
 */
export function isValidZipcodePattern(zipcode: string, validationPattern: string): boolean {
  return new RegExp(validationPattern).test(zipcode);
}

/**
 * Validates zipcode with all validation rules:
 * - Non-empty check
 * - Pattern matching (regex)
 */
export function validateZipcode(
  zipcode: string,
  options: {
    validation?: ZipcodeValidation;
  },
): boolean {
  const { validation } = options;

  if (zipcode === "") return false;

  if (validation) {
    if (!isValidZipcodeLength(zipcode, validation)) return false;
    if (validation.validationPattern && !isValidZipcodePattern(zipcode, validation.validationPattern)) return false;
  }

  return true;
}
